'use client';
import React, { useState } from 'react';
import { MapPin, Clock, Cpu, ExternalLink, CheckCircle, Play } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { Briefcase } from 'lucide-react';
import { TechJob } from './technicianTypes';
import JobCompletionForm from './JobCompletionForm';

interface Props {
  jobs: TechJob[];
  onStartJob: (id: string) => void;
  onCompleteJob: (id: string, data: {
    paymentMode: TechJob['paymentMode'];
    filters: TechJob['filters'];
    actualStartTime: string;
    actualEndTime: string;
  }) => void;
}

const statusBadge: Record<string, 'info' | 'warning' | 'active'> = {
  pending: 'info',
  'in-progress': 'warning',
  completed: 'active',
};

export default function JobQueue({ jobs, onStartJob, onCompleteJob }: Props) {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const sorted = [...jobs].sort((a, b) => {
    const order = { pending: 0, 'in-progress': 1, completed: 2 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return a.scheduledTime.localeCompare(b.scheduledTime);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-700 text-foreground">Assigned Jobs</h2>
        <span className="text-xs text-muted-foreground">{jobs.length} total · ordered by scheduled time</span>
      </div>

      {sorted.length === 0 ? (
        <div className="bg-card rounded-xl border border-border">
          <EmptyState
            icon={<Briefcase size={28} />}
            title="No jobs assigned today"
            description="Your assigned service jobs will appear here. Contact admin if you believe jobs should be assigned."
          />
        </div>
      ) : (
        sorted.map((job) => (
          <div
            key={job.id}
            className={`bg-card rounded-xl border transition-all duration-200 overflow-hidden ${
              job.status === 'in-progress' ?'border-amber-300 shadow-md shadow-amber-100'
                : job.status === 'completed' ?'border-green-200 opacity-75' :'border-border'
            }`}
          >
            <div className="p-4">
              {/* Job header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-700 text-sm ${
                    job.status === 'completed' ? 'bg-green-100 text-success' :
                    job.status === 'in-progress'? 'bg-amber-100 text-amber-700' : 'bg-secondary text-primary'
                  }`}>
                    {job.status === 'completed' ? <CheckCircle size={20} /> : (
                      <span className="tabular-nums">{job.scheduledTime.split(':')[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-700 text-foreground">{job.customerName}</span>
                      <span className="font-mono text-xs text-primary bg-secondary px-1.5 py-0.5 rounded">{job.customerNumber}</span>
                      <Badge variant={statusBadge[job.status]}>
                        {job.status === 'in-progress' ? 'In Progress' : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {job.scheduledTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Cpu size={11} />
                        {job.machineModel}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    className="p-2 rounded-lg hover:bg-secondary text-primary transition-colors"
                    title="View customer profile"
                    aria-label="View customer profile"
                  >
                    <ExternalLink size={15} />
                  </button>
                  {job.status === 'pending' && (
                    <button
                      onClick={() => onStartJob(job.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                    >
                      <Play size={12} />
                      Start
                    </button>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="mt-3 flex items-start gap-1.5 text-sm text-muted-foreground">
                <MapPin size={13} className="mt-0.5 text-primary shrink-0" />
                <div>
                  <span>{job.address}</span>
                  {job.landmark && <span className="text-xs ml-1 text-primary font-600">· {job.landmark}</span>}
                </div>
              </div>

              {/* Filter pills */}
              <div className="mt-2 flex gap-1 flex-wrap">
                {(['sedi', 'pre', 'mem', 'post'] as const).map((f) => (
                  <span
                    key={`jq-${job.id}-${f}`}
                    className={`text-xs px-2 py-0.5 rounded font-600 ${
                      job.filters[f] ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </span>
                ))}
                <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground font-600">{job.paymentMode}</span>
              </div>

              {/* Expanded: customer profile + completion form */}
              {expandedJob === job.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-4 fade-in">
                  {/* Customer profile snapshot */}
                  <div className="bg-secondary rounded-xl p-4">
                    <p className="text-xs font-600 uppercase tracking-wide text-muted-foreground mb-2">Customer Profile</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Full Name</p>
                        <p className="font-600 text-foreground">{job.customerName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Customer #</p>
                        <p className="font-mono font-600 text-primary">{job.customerNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Machine Model</p>
                        <p className="font-600 text-foreground">{job.machineModel}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Address</p>
                        <p className="font-600 text-foreground text-xs">{job.address}</p>
                      </div>
                    </div>
                    {job.notes && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground">Notes</p>
                        <p className="text-sm text-foreground mt-0.5">{job.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Completion form */}
                  {job.status === 'in-progress' && (
                    <JobCompletionForm
                      job={job}
                      onComplete={(data) => {
                        onCompleteJob(job.id, data);
                        setExpandedJob(null);
                      }}
                    />
                  )}

                  {job.status === 'completed' && (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-success">
                      <CheckCircle size={16} />
                      <span className="font-600">Job completed · {job.actualStartTime} – {job.actualEndTime}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}